import {
  ArrowLeftRight,
  Banknote,
  CircleHelp,
  CreditCard,
  HandCoins,
  Landmark,
  Scale,
  TrendingUp,
} from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"

import { Aparece } from "@/componentes/ui/Aparece"
import { Pestanas } from "@/componentes/ui/Pestanas"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

import { Bancos } from "./Bancos"
import { Ingresos } from "./Ingresos"
import { MetodosPago } from "./MetodosPago"
import { Movimientos } from "./Movimientos"
import { PagosPorEjecutar } from "./PagosPorEjecutar"
import { SaldosProveedores } from "./SaldosProveedores"
import { Tasas } from "./Tasas"

export const VISTAS = [
  { valor: "tasas", etiqueta: "Tasas de cambio", icono: TrendingUp },
  { valor: "bancos", etiqueta: "Bancos", icono: Landmark },
  { valor: "movimientos", etiqueta: "Movimientos", icono: ArrowLeftRight },
  // El dinero que entra con su pagador (14/10). Permiso propio: ver lo que debe cada
  // cliente y cada empleado no es ver el extracto de los bancos.
  { valor: "ingresos", etiqueta: "Ingresos", icono: Banknote, permiso: "ingreso:ver" },
  // ⚠ Esta pestaña pide ADEMAS su propio permiso: ver tesoreria no es poder pagar.
  // Sin `tesoreria:ejecutar_pago` no se ofrece, igual que las pestañas del Catalogo.
  {
    valor: "pagos",
    etiqueta: "Pagos por ejecutar",
    icono: HandCoins,
    permiso: "tesoreria:ejecutar_pago",
  },
  { valor: "proveedores", etiqueta: "Saldos de proveedores", icono: Scale },
  { valor: "metodos", etiqueta: "Métodos de pago", icono: CreditCard },
] as const

type Vista = (typeof VISTAS)[number]["valor"]

/** Direcciones de cuando cada vista era su propia entrada del menú. */
const ANTIGUAS: Record<string, Vista> = { cuentas: "bancos" }

/**
 * La frase bajo el título, la de la pestaña que se está mirando. Cada vista tenía la
 * suya encima de su contenido; en la cabecera no repite el título y deja la primera
 * línea de la vista para sus acciones, como en len-platform.
 */
const SUBTITULO: Record<Vista, string> = {
  tasas:
    "Lo que cuesta cada divisa en bolívares y cómo ha ido. El dólar BCV es la tasa referencial de todo el sistema.",
  bancos: "Las cuentas de la agencia y todo lo que ha pasado por ellas.",
  movimientos:
    "El ledger de banco, de todas las cuentas. Las anuladas se marcan; no se borra ninguna.",
  ingresos:
    "El dinero que entra, con su soporte y quién lo pagó: cliente, proveedor o empleado. Si no se sabe todavía, queda por determinar.",
  pagos: "Las órdenes aprobadas que esperan a que salga el dinero. Las más antiguas, primero.",
  proveedores:
    "Lo que la agencia le debe a cada proveedor y lo que cada proveedor le debe a la agencia, por moneda.",
  metodos: "Cómo sale el dinero y cuánto suele cobrar el banco por ello.",
}

/** El «?»: las reglas de cada vista que la pantalla no dice sola. */
const AYUDA: Record<Vista, readonly string[]> = {
  tasas: [
    "Se leen en bolívares por 1 unidad. El sistema las guarda contra el dólar —euros o USDT por 1 USD— y las convierte con el dólar BCV de ese día.",
    "Las del BCV y la de Binance llegan solas a las 8:30 y a las 17:30. «Registrar a mano» es para cuando no publican o hay que asentar la que de verdad se usó.",
    "La variación de cada tarjeta es contra el cierre del día anterior con tasa: el BCV no publica los fines de semana.",
    "Una tasa registrada no se corrige: se registra otra, y para esa fecha gana la última.",
  ],
  bancos: [
    "Cada clase de cuenta pide lo suyo: un banco venezolano su número y su banco del catálogo, una billetera su dirección y su red, el efectivo dónde está.",
    "Una cuenta puede llevar PIN. Entonces se pide una vez por sesión, y sin él no se ve su saldo, ni su extracto, ni se paga desde ella.",
    "El saldo de cada cuenta sale de sus movimientos: no hay un número que alguien teclee.",
    "El extracto lleva el saldo después de cada movimiento. Una anulada se queda tachada y no cuenta.",
    "Los totales van por divisa: sumar bolívares con dólares exigiría convertir, y el total cambiaría con la tasa sin que se moviera un céntimo.",
  ],
  movimientos: [
    "Un movimiento no se borra: se anula con su motivo, y la fila se queda.",
    "La fecha de la tabla es la fecha valor, el día en que el banco movió el dinero; cuándo se registró sale al posar el ratón encima, y es lo que no se puede antedatar.",
    "Se ven los más recientes, con tope, y «ver más» trae otra tanda. Para lo anterior, filtra por fechas o busca por concepto o referencia.",
  ],
  ingresos: [
    "Reportar un ingreso lo registra en el banco al momento, con su soporte. Si no se sabe quién lo pagó, queda «por determinar» y se le asigna después desde el menú de la fila.",
    "De un cliente, cobra sus cuentas por cobrar abiertas de la misma moneda —de la más antigua a la más reciente, o como se reparta— y lo que sobra queda de anticipo, que se aplica después.",
    "De un proveedor, primero cubre el saldo que la empresa tenga con él; lo que pase de ahí queda como reembolso en su estado de cuenta.",
    "De un empleado, abona lo que debe por anticipos y préstamos: los pagos puntuales de Nómina marcados así.",
    "«Leer con IA» propone monto, fecha, referencia, la cuenta donde entró y quién lo pagó. Nada se usa sin confirmar.",
    "Quitar el pagador deshace lo que hizo en su cuenta, pero el dinero sigue en el banco. Anular el ingreso anula también su movimiento.",
  ],
  pagos: [
    "Pagar una orden debita la cuenta que elijas: el pago, la comisión y el IGTF salen como tres movimientos, igual que en el extracto del banco.",
    "El comprobante es obligatorio. Sin él no se registra el pago.",
    "Revertir un pago —o anular cualquiera de sus movimientos aquí en Tesorería— devuelve la orden a aprobada y el dinero a la cuenta.",
  ],
  proveedores: [
    "«A favor del proveedor» es lo que la agencia le debe: órdenes aprobadas sin pagar y el resto de los pagos hechos de menos.",
    "«Disponible de la empresa» es lo que se le pagó de más: se le descuenta al pagarle otra orden, desde el modal de pago.",
    "Nada se teclea: sale de las órdenes y sus pagos, así que revertir o corregir un pago corrige el saldo.",
    "Pulsa un proveedor para ver su estado de cuenta con el saldo después de cada partida.",
  ],
  metodos: [
    "La comisión es una sugerencia para el pago: al ejecutar se teclea la que cobró el banco.",
    "Los importes fijo, mínimo y máximo van en la moneda de la cuenta que paga.",
  ],
}

function vistaDe(enRuta: string | undefined): Vista {
  if (enRuta === undefined) return "tasas"
  return VISTAS.find((v) => v.valor === enRuta)?.valor ?? ANTIGUAS[enRuta] ?? "tasas"
}

/**
 * Tesorería: UNA entrada del menú con sus vistas en pestañas, como el módulo de bancos
 * de len-platform —título con su «?», la frase de la pestaña debajo y las pestañas con
 * icono—.
 *
 * ⚠ **Fueron cuatro entradas —tasas, bancos, cuentas y movimientos— y el menú crecía
 * una línea por cada vista de lo mismo.** Las cuentas ya no tienen pestaña propia:
 * viven en «Bancos», a la izquierda de su ficha.
 *
 * ⚠ **La vista va en la URL** (`/tesoreria/bancos`), no sólo en el estado: se puede
 * recargar y compartir. Las direcciones viejas siguen llevando a algún sitio —
 * `/tesoreria/cuentas` abre «Bancos»— y la barra se corrige a la buena.
 *
 * ⚠ **Se cambia de pestaña con `replace`**, sin apilar historial: recorrer las cuatro
 * con las flechas no puede obligar a pulsar «atrás» cuatro veces para salir.
 *
 * ⚠ **El título y las pestañas se van al bajar, y así tiene que ser.** Estuvieron
 * pegados arriba (`.encabezado-pegajoso`) y se quitó: la cabecera de este módulo ocupa
 * tres líneas y las pestañas otra, y encima de un extracto largo eso no era una ayuda
 * sino una banda que se comía la pantalla y tapaba las filas al pasar por debajo. Si
 * vuelve a plantearse, la página se desplaza entera: nada fijo.
 *
 * ⚠ **Se monta sólo la pestaña activa.** «Bancos» pide el resumen de cada cuenta;
 * dispararlo en cada visita a las tasas sería pagar esas consultas para no verlas.
 *
 * ⚠ **El «?» se despliega en el sitio y va FUERA del `<h1>`.** En len-platform es un
 * enlace a su guía, y aquí no hay guía. Y dentro del título, el nombre del encabezado
 * pasaría a ser «Tesorería Cómo funciona esta pestaña» para quien navega por títulos.
 */
export function Tesoreria() {
  const { vista: enRuta } = useParams()
  const navegar = useNavigate()
  const usuario = usarSesion((e) => e.usuario)
  const visibles = VISTAS.filter((v) => !("permiso" in v) || tienePermiso(usuario, v.permiso))
  // Una pestaña que no se puede abrir cae a la primera: la URL no abre lo que el
  // menú no ofrece —y el servidor, de todos modos, no lo serviría—.
  const pedida = vistaDe(enRuta)
  const vista = visibles.some((v) => v.valor === pedida) ? pedida : "tasas"
  const [ayuda, setAyuda] = useState(false)

  useEffect(() => {
    if (enRuta !== undefined && enRuta !== vista) navegar(`/tesoreria/${vista}`, { replace: true })
  }, [enRuta, vista, navegar])

  return (
    <div>
      <header className="mb-4">
        <div className="flex items-center gap-1">
          <h1 className="text-xl font-semibold">Tesorería</h1>
          <button
            type="button"
            aria-expanded={ayuda}
            aria-controls={ayuda ? "ayuda-tesoreria" : undefined}
            onClick={() => setAyuda((a) => !a)}
            className="foco-cristal toque-cristal inline-flex size-7 items-center justify-center rounded-[var(--radius-control)]"
            style={{ color: "var(--fg-muted)" }}
          >
            <CircleHelp size={16} aria-hidden={true} />
            <span className="sr-only">Cómo funciona esta pestaña</span>
          </button>
        </div>
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {SUBTITULO[vista]}
        </p>
        {/* Con aire abajo: `Aparece` recorta lo que se sale, y la sombra de la tarjeta
            se saldría. */}
        <Aparece visible={ayuda} className="pb-1">
          <div id="ayuda-tesoreria" className="tarjeta mt-3 max-w-2xl">
            <ul className="list-disc space-y-1 pl-5 text-sm">
              {AYUDA[vista].map((linea) => (
                <li key={linea}>{linea}</li>
              ))}
            </ul>
          </div>
        </Aparece>
      </header>

      <Pestanas
        pestanas={visibles}
        activa={vista}
        onCambiar={(v) => navegar(`/tesoreria/${v}`, { replace: true })}
        etiqueta="Vistas de tesorería"
        idPanel="panel-tesoreria"
        prefijoId="pestana-tesoreria"
      />

      <div id="panel-tesoreria" role="tabpanel" aria-labelledby={`pestana-tesoreria-${vista}`}>
        {vista === "tasas" && <Tasas />}
        {vista === "bancos" && <Bancos />}
        {vista === "movimientos" && <Movimientos />}
        {vista === "ingresos" && <Ingresos />}
        {vista === "pagos" && <PagosPorEjecutar />}
        {vista === "proveedores" && <SaldosProveedores />}
        {vista === "metodos" && <MetodosPago />}
      </div>
    </div>
  )
}
